import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useBluelagoon } from "@bluelagoon";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shadcn-in-nx/ui/table";
import { Button } from "@shadcn-in-nx/ui/button";
import { Loader2, Layers, Archive, ExternalLink } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const PAGE_SIZE = 25;

export default function WorkflowsTab({ companyId }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [workflows, setWorkflows] = useState([]);
    const [showArchived, setShowArchived] = useState(false);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const { call: fetchWorkflows, isLoading } = useBluelagoon({ method: "getAllAdminWorkflows" });

    const load = useCallback(() => {
        fetchWorkflows({
            companyId,
            limit: PAGE_SIZE,
            offset: (page - 1) * PAGE_SIZE,
            includeArchived: showArchived || undefined,
        })
            .then((res) => {
                setWorkflows(res?.workflows || []);
                setTotal(res?.pagination?.total || 0);
            })
            .catch(() => setWorkflows([]));
    }, [fetchWorkflows, companyId, page, showArchived]);

    useEffect(() => { load(); }, [load]);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <Button size="sm" variant={showArchived ? "default" : "outline"} onClick={() => { setShowArchived((v) => !v); setPage(1); }}>
                    <Archive className="h-3 w-3 mr-1" />
                    {showArchived ? t("admin.manageCompany.workflows.hideArchived", "Hide archived") : t("admin.manageCompany.workflows.showArchived", "Show archived")}
                </Button>
                <span className="text-xs text-muted-foreground">{t("admin.manageCompany.workflows.total", "{{count}} workflows", { count: total })}</span>
            </div>
            {isLoading && workflows.length === 0 ? (
                <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            ) : (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{t("admin.manageCompany.workflows.name", "Name")}</TableHead>
                            <TableHead>{t("admin.manageCompany.workflows.type", "Type")}</TableHead>
                            <TableHead className="text-right">{t("admin.manageCompany.workflows.steps", "Steps")}</TableHead>
                            <TableHead>{t("admin.manageCompany.workflows.status", "Status")}</TableHead>
                            <TableHead>{t("admin.manageCompany.workflows.updated", "Updated")}</TableHead>
                            <TableHead className="w-12" />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {workflows.length === 0 ? (
                            <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                                {t("admin.manageCompany.workflows.empty", "No workflows")}
                            </TableCell></TableRow>
                        ) : workflows.map((wf) => (
                            <TableRow key={wf.id} className="cursor-pointer hover:bg-muted/40" onClick={() => navigate(`/admin/workflows/${wf.id}`)}>
                                <TableCell className="font-medium">{wf.name}</TableCell>
                                <TableCell><span className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700">{wf.workflowType || "workflow"}</span></TableCell>
                                <TableCell className="text-right"><span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Layers className="h-3 w-3" />{wf._count?.steps || 0}</span></TableCell>
                                <TableCell>
                                    <span className={`text-xs px-2 py-0.5 rounded ${wf.archivedAt ? "bg-gray-100 text-gray-600" : "bg-green-100 text-green-700"}`}>
                                        {wf.archivedAt ? t("admin.manageCompany.workflows.archived", "Archived") : t("admin.manageCompany.workflows.active", "Active")}
                                    </span>
                                </TableCell>
                                <TableCell className="text-xs text-muted-foreground">{wf.updatedAt ? formatDistanceToNow(new Date(wf.updatedAt), { addSuffix: true }) : "—"}</TableCell>
                                <TableCell><ExternalLink className="h-3 w-3 text-muted-foreground" /></TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
            {totalPages > 1 && (
                <div className="flex items-center justify-between text-xs">
                    <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>{t("common.previous", "Previous")}</Button>
                    <span className="text-muted-foreground">{t("common.pageOf", "Page {{page}} of {{total}}", { page, total: totalPages })}</span>
                    <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>{t("common.next", "Next")}</Button>
                </div>
            )}
        </div>
    );
}
